/**
 * Checkout Page Component
 *
 * Pagina checkout con layout coerente al template PetLox
 * Form dati spedizione + riepilogo ordine
 */

import React, { useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { loadCart, clearCartAction } from '../../store/actions/cartActions';
import PayPalButton from './PayPalButton';
import StripeButton from './StripeButton';
import Navbar from '../Navbar/Navbar';
import PageTitle from '../pagetitle/PageTitle';
import Scrollbar from '../scrollbar/scrollbar';
import Footer from '../footer/Footer';
import logo from '../../images/gattomioNero.png';

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector(state => state.cartAPI);
  const { user } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    client_email: user?.email || '',
    client_name: user?.name || '',
    shipping_address: user?.address || '',
    shipping_city: user?.city || '',
    shipping_zip: user?.zip || '',
    shipping_country: user?.country || 'IT',
  });

  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal'); // 'paypal' | 'stripe'

  useEffect(() => {
    dispatch(loadCart());
  }, [dispatch]);

  useEffect(() => {
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const errors = {};

    if (!formData.client_email) {
      errors.client_email = 'Email richiesta';
    } else if (!/\S+@\S+\.\S+/.test(formData.client_email)) {
      errors.client_email = 'Email non valida';
    }

    if (!formData.client_name) {
      errors.client_name = 'Nome richiesto';
    }

    if (!formData.shipping_address) {
      errors.shipping_address = 'Indirizzo richiesto';
    }

    if (!formData.shipping_city) {
      errors.shipping_city = 'Città richiesta';
    }

    if (!formData.shipping_zip) {
      errors.shipping_zip = 'CAP richiesto';
    } else if (!/^\d{4,10}$/.test(formData.shipping_zip)) {
      errors.shipping_zip = 'CAP non valido';
    }

    if (!formData.shipping_country) {
      errors.shipping_country = 'Paese richiesto';
    }

    setFormErrors(errors);
    setIsFormValid(Object.keys(errors).length === 0);

    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const handlePaymentSuccess = (order) => {
    dispatch(loadCart());
    navigate(`/order-success/${order.id}`);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  // Loading state
  if (cart.loading && !cart.items.length) {
    return (
      <Fragment>
        <Navbar hclass={'wpo-site-header'} Logo={logo} />
        <PageTitle pageTitle={'Checkout'} pagesub={'Checkout'} />
        <section className="wpo-checkout-section section-padding">
          <div className="container">
            <div className="loading-state">
              <div className="spinner">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </div>
              </div>
              <p>Caricamento carrello...</p>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  // Empty cart state
  if (!cart.items || cart.items.length === 0) {
    return (
      <Fragment>
        <Navbar hclass={'wpo-site-header'} Logo={logo} />
        <PageTitle pageTitle={'Checkout'} pagesub={'Checkout'} />
        <section className="wpo-checkout-section section-padding">
          <div className="container">
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fi flaticon-shopping-cart"></i>
              </div>
              <h3>Il tuo carrello è vuoto</h3>
              <p>Aggiungi prodotti dal catalogo per procedere al checkout!</p>
              <Link onClick={ClickHandler} className="return-btn" to="/shop">
                Vai allo Shop
              </Link>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  const subtotal = (cart.items || []).reduce((sum, item) => {
    const price = parseFloat(item.product?.price || item.price_snapshot || 0);
    return sum + price * item.quantity;
  }, 0);
  const iva = subtotal * 0.22;
  const totalAmount = (subtotal + iva).toFixed(2);

  return (
    <Fragment>
      <Navbar hclass={'wpo-site-header'} Logo={logo} />
      <PageTitle pageTitle={'Checkout'} pagesub={'Checkout'} />

      <section className="wpo-checkout-section section-padding">
        <div className="container">
          <div className="checkout-wrapper">
            {/* Form Panel */}
            <div className="checkout-form-panel">
              <span className="panel-header">Dati Spedizione</span>
              <h2>Completa l'ordine</h2>

              <div className="checkout-form">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="client_email"
                    placeholder="tuo@email.com"
                    value={formData.client_email}
                    onChange={handleInputChange}
                    className={formErrors.client_email ? 'error' : ''}
                  />
                  {formErrors.client_email && (
                    <span className="error-text">{formErrors.client_email}</span>
                  )}
                  <span className="help-text">Riceverai la conferma ordine a questa email</span>
                </div>

                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    name="client_name"
                    placeholder="Mario Rossi"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    className={formErrors.client_name ? 'error' : ''}
                  />
                  {formErrors.client_name && (
                    <span className="error-text">{formErrors.client_name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Indirizzo *</label>
                  <input
                    type="text"
                    name="shipping_address"
                    placeholder="Via Roma 123"
                    value={formData.shipping_address}
                    onChange={handleInputChange}
                    className={formErrors.shipping_address ? 'error' : ''}
                  />
                  {formErrors.shipping_address && (
                    <span className="error-text">{formErrors.shipping_address}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Città *</label>
                    <input
                      type="text"
                      name="shipping_city"
                      placeholder="Milano"
                      value={formData.shipping_city}
                      onChange={handleInputChange}
                      className={formErrors.shipping_city ? 'error' : ''}
                    />
                    {formErrors.shipping_city && (
                      <span className="error-text">{formErrors.shipping_city}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>CAP *</label>
                    <input
                      type="text"
                      name="shipping_zip"
                      placeholder="20100"
                      value={formData.shipping_zip}
                      onChange={handleInputChange}
                      maxLength="10"
                      className={formErrors.shipping_zip ? 'error' : ''}
                    />
                    {formErrors.shipping_zip && (
                      <span className="error-text">{formErrors.shipping_zip}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Paese *</label>
                  <select
                    name="shipping_country"
                    value={formData.shipping_country}
                    onChange={handleInputChange}
                    className={formErrors.shipping_country ? 'error' : ''}
                  >
                    <option value="IT">Italia</option>
                    <option value="FR">Francia</option>
                    <option value="DE">Germania</option>
                    <option value="ES">Spagna</option>
                    <option value="CH">Svizzera</option>
                    <option value="AT">Austria</option>
                    <option value="BE">Belgio</option>
                    <option value="NL">Paesi Bassi</option>
                    <option value="GB">Regno Unito</option>
                    <option value="US">Stati Uniti</option>
                  </select>
                  {formErrors.shipping_country && (
                    <span className="error-text">{formErrors.shipping_country}</span>
                  )}
                </div>

                <p className="form-required">* Campi obbligatori</p>
              </div>
            </div>

            {/* Summary Panel */}
            <div className="checkout-summary-panel">
              <span className="panel-header">Riepilogo Ordine</span>
              <h2>Totale</h2>

              <div className="summary-card">
                <div className="summary-items">
                  {cart.items && cart.items.map((item) => (
                    <div className="summary-item" key={item.id}>
                      <div className="item-info">
                        <div className="item-name">{item.product?.name || 'Prodotto'}</div>
                        <div className="item-qty">Quantità: {item.quantity}</div>
                      </div>
                      <div className="item-price">
                        €{((item.product?.price || item.price_snapshot || 0) * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="summary-totals">
                  <div className="total-row">
                    <span className="label">Subtotale:</span>
                    <span className="value">€{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="total-row">
                    <span className="label">IVA (22%):</span>
                    <span className="value">€{iva.toFixed(2)}</span>
                  </div>
                  <div className="total-row">
                    <span className="label">Spedizione:</span>
                    <span className="value">Gratuita</span>
                  </div>
                  <div className="total-row">
                    <span className="label">Totale:</span>
                    <span className="value highlight">€{totalAmount}</span>
                  </div>
                </div>

                <div className="payment-button-area">

                  {/* Selettore metodo di pagamento */}
                  <div className="payment-method-selector" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: paymentMethod === 'paypal' ? '2px solid #c8a97e' : '2px solid #ddd',
                        borderRadius: '8px',
                        background: paymentMethod === 'paypal' ? '#fff8f0' : '#fff',
                        cursor: 'pointer',
                        fontWeight: paymentMethod === 'paypal' ? '600' : '400',
                      }}
                    >
                      PayPal
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('stripe')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: paymentMethod === 'stripe' ? '2px solid #c8a97e' : '2px solid #ddd',
                        borderRadius: '8px',
                        background: paymentMethod === 'stripe' ? '#fff8f0' : '#fff',
                        cursor: 'pointer',
                        fontWeight: paymentMethod === 'stripe' ? '600' : '400',
                      }}
                    >
                      Carta di credito
                    </button>
                  </div>

                  {/* Form non valido: mostra bottone disabilitato */}
                  {!isFormValid && (
                    <button className="submit-btn" disabled title="Completa il modulo per procedere">
                      Completa il modulo
                    </button>
                  )}

                  {/* PayPal */}
                  {isFormValid && paymentMethod === 'paypal' && (
                    <PayPalButton
                      cartTotal={parseFloat(totalAmount)}
                      shippingData={formData}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  )}

                  {/* Stripe */}
                  {isFormValid && paymentMethod === 'stripe' && (
                    <StripeButton
                      shippingData={formData}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default CheckoutPage;
